/* eslint-disable max-lines */
import { isEmpty, isNullOrUndefined } from '@app/util/util';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { FilterTypes } from '@shared/flight-itinerary/entities/filters';
import { SearchPayload, AirportsFilter, NewAirSearchPayload } from '@flight-results/entities/request-search.payload';
import {
  PickedRange, RangePicketVm, CheckboxFilterVm, CheckboxFilter
} from '@shared/filters/entities/fetch-filters.response';
import { TripTypes } from '@app/entities/trip-types';
import { AirResultPackagesPayload } from '@packages/packages-result/entities/air-results';
import { mapFiltersReduce } from '@app/mappers/filters.mapper';
import { SORTER_BY_AUX_CRITERIA_TYPE, SORTER_TITLES } from '@flight-results/constants/sorters.constants';
import { AirFiltersResponse } from '@shared/magic-filter-bar/entities/filter-types';

export const SECONDS_IN_HOURS = 3600;

export function pickedCheckboxes(searchPayload: SearchPayload): string[] {
  return isEmpty(searchPayload) || Object.keys(searchPayload).length === 0 ? [] :
    (searchPayload.Stops || []).concat((searchPayload.Alliances || []))
      .concat((searchPayload.Airlines || [])).concat((searchPayload.BusClass || []))
      .concat((searchPayload.Tags || []));
}

export function pickedAirportCheckboxes(searchPayload: SearchPayload): AirportsFilter {
  return {
    DepartureAirports: searchPayload?.[FilterTypes.DepartureAirports] || [],
    ArrivalAirports: searchPayload?.[FilterTypes.ArrivalAirports] || []
  };
}

function pickedDurationsRanges(searchPayload: SearchPayload | AirResultPackagesPayload, type: FilterTypes): RangePicketVm {
  const departureRange = searchPayload[type] ? searchPayload[type].find(sector =>
    sector.SectorNumber === TripTypes.DEPARTURE) : null;
  const returnRange = searchPayload[type] ? searchPayload[type].find(sector =>
    sector.SectorNumber === TripTypes.RETURN) : null;
  return {
    [TripTypes.DEPARTURE]: !isNullOrUndefined(departureRange) ?
      [stringTimeToSeconds(departureRange.From), stringTimeToSeconds(departureRange.To)] : [],
    [TripTypes.RETURN]: !isNullOrUndefined(returnRange) ?
      [stringTimeToSeconds(returnRange.From), stringTimeToSeconds(returnRange.To)] : []
  };
}

export function pickedRanges(searchPayload: SearchPayload | AirResultPackagesPayload): PickedRange {
  return isEmpty(searchPayload) || Object.keys(searchPayload).length === 0 ? {} as PickedRange :
    isNullOrUndefined(searchPayload.PriceFrom) && isNullOrUndefined(searchPayload.PriceTo) ? {
      [FilterTypes.SectorDurations]: pickedDurationsRanges(searchPayload, FilterTypes.SectorDurations),
      [FilterTypes.DepartureTimes]: pickedDurationsRanges(searchPayload, FilterTypes.DepartureTimes)
    } :
      {
        [FilterTypes.PriceRange]: [searchPayload.PriceFrom, searchPayload.PriceTo],
        [FilterTypes.SectorDurations]: pickedDurationsRanges(searchPayload, FilterTypes.SectorDurations),
        [FilterTypes.DepartureTimes]: pickedDurationsRanges(searchPayload, FilterTypes.DepartureTimes)
      };
}

export function stringTimeToSeconds(time: string): number {
  let timeInSeconds = 0;
  const timeWithDaysParts = time?.toString()?.match(/(\d+)\.(\d+)\:(\d+)\:(\d+)/);
  if (timeWithDaysParts) {
    const days = Math.floor(parseFloat(timeWithDaysParts[1]) * 24 * 60 * 60);
    const hours = Math.floor(parseFloat(timeWithDaysParts[2]) * 60 * 60);
    const minutes = Math.floor(parseFloat(timeWithDaysParts[3]) * 60);
    const seconds = Math.floor(parseFloat(timeWithDaysParts[4]));
    timeInSeconds = days + hours + minutes + seconds;
  } else {
    const timeParts = time?.toString()?.match(/(\d+)\:(\d+)\:(\d+)/);
    const hours = Math.floor(parseFloat(timeParts?.[1]) * 60 * 60);
    const minutes = Math.floor(parseFloat(timeParts?.[2]) * 60);
    const seconds = Math.floor(parseFloat(timeParts?.[3]));
    timeInSeconds = hours + minutes + seconds;
  }
  return timeInSeconds;
}

export function secondsToStringTime(seconds: number, removeSeconds: boolean = false, hourFormat: boolean = true): string {
  const days = Math.floor(seconds / 60 / 60 / 24);
  const hours = Math.floor(seconds / 60 / 60) % 24;
  const minutes = Math.floor(seconds / 60) % 60;
  const secs = Math.floor(seconds) % 60;
  const hoursStr = (hours === 0 && minutes === 0 && days === 1) ? 24 : hours < 10 ? `0${hours}` : hours;
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
  const secsStr = secs < 10 ? `0${secs}` : secs;
  return (hoursStr === 24 && !hourFormat) ? 
    `${days}.00:${minutesStr}${removeSeconds ? '' : `:${secsStr}`}`: (days === 0 || hoursStr === 24)  ?
      `${hoursStr}:${minutesStr}${removeSeconds ? '' : `:${secsStr}`}` :
      `${days}.${hoursStr}:${minutesStr}${removeSeconds ? '' : `:${secsStr}`}`;
}

export const timeToNumber = (number: string): string => {
  const _number = number.split(':');
  const hour = parseFloat(_number[0]);
  const minutes = parseFloat(_number[1]) / 60;
  return (hour + minutes).toString();
};

export function resetFilters(searchPayload: NewAirSearchPayload): NewAirSearchPayload {
  return {
    ...searchPayload,
    Results: {
      ...searchPayload.Results,
      Airlines: [],
      Alliances: [],
      Stops: [],
      Tags: [],
      SectorDurations: [],
      DepartureTimes: [],
      PriceFrom: undefined,
      PriceTo: undefined,
      DepartureAirports: [],
      ArrivalAirports: [],
      OrderCriteria: null,
      OrderDesc: false,
      OrderCriteriaSectorNumber: null
    }
  };
}

export function mapAirlines(airlines: CheckboxFilter[]): CheckboxFilterVm[] {
  const airlinesArray = [...new Set(airlines.map(a => a.Text))];
  return airlinesArray.map(text => {
    return {
      Text: text,
      Count: airlines.filter(a => a.Text === text).reduce((acc, item) => acc += item.Count, 0),
      MinPrice: Math.min(...airlines.filter(a => a.Text === text).map(item => item.MinPrice)),
      Label: airlines.find(a => a.Text === text).AirlineName || text
    };
  }).sort((a, b) => b.Count - a.Count);
}

export function mapFiltersSearchFlights(filters: AirFiltersResponse, resultsCounts: number): AirFiltersResponse {
  return mapFiltersReduce(filters, resultsCounts) as AirFiltersResponse;
}

export function mapSorterLabel(type: SorterTypes, sectorNumber: number, orderDesc: boolean): string {
  if (type === SorterTypes.Departure || type === SorterTypes.Arrival) {
    return SORTER_BY_AUX_CRITERIA_TYPE[getOrderCriteriaAux(type, sectorNumber, orderDesc)];
  } else {
    return SORTER_TITLES[type];
  }
}

export function getOrderCriteriaAux(type: SorterTypes, sectorNumber: number, orderDesc: boolean): string {
  if (type === SorterTypes.Departure || type === SorterTypes.Arrival) {
    const timer = orderDesc ? 'LAST' : 'EARLIEST';
    return `${timer}_${type}_${sectorNumber}`;
  } else {
    return type;
  }
}
